import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, ShoppingCart, CreditCard, Truck } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function RetailPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Retail & E-commerce',
    description: 'Protecting customer data, payment systems, and supply chains',
    url: 'https://thecisonetwork.com/sectors/retail',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Retail & E-commerce',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Retail & E-commerce"
        subtitle="Protecting customer data, payment systems, and supply chains in the digital retail era"
        eyebrow="Retail & E-commerce"
        backgroundImage={getSectorCard('retail')?.image}
        backgroundAlt={getSectorCard('retail')?.imageAlt}
        seoTitle="Fractional CISO Retail | The CISO Network"
        seoDescription="Expert fractional CISO services for retail and e-commerce. Protect customer data, ensure PCI-DSS compliance, and prevent costly data breaches."
      />

      {/* Why Retail Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Retail
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Retail has been transformed by digital disruption. E-commerce now accounts for
                  over 30% of UK retail sales, while brick-and-mortar stores increasingly rely on
                  connected point-of-sale systems, inventory management platforms, and customer
                  loyalty programmes. This digitalisation has made retailers prime targets -
                  handling payment card data, personal information, and transaction records that
                  attackers covet.
                </p>
                <p>
                  The sector faces unique challenges. Retailers process millions of transactions
                  across complex supply chains involving suppliers, logistics partners, payment
                  processors, and cloud platforms. Peak shopping periods like Black Friday create
                  massive traffic spikes that strain security controls. Meanwhile, the shift to
                  omnichannel retail - connecting online, mobile, and in-store experiences - expands
                  the attack surface dramatically.
                </p>
                <p>
                  Regulatory and financial consequences of breaches are severe.{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    PCI-DSS violations
                  </Link>{' '}
                  can result in fines from £4,000 to £90,000 per month, plus liability for
                  fraudulent transactions. GDPR penalties add another layer of exposure. The
                  reputational damage can be catastrophic - customers lose trust quickly when their
                  payment details are compromised, and social media amplifies every incident.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <CreditCard className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      PCI-DSS Expertise
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate payment card compliance with confidence. Our CISOs understand PCI-DSS
                      requirements, secure payment architecture, and how to protect cardholder data.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <ShoppingCart className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      E-commerce Security
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Protect online stores, mobile apps, and digital payment flows. Our CISOs
                      understand the unique threats facing digital retailers and how to defend
                      against them.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Retail's Costly Breaches
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real incidents where retailers suffered breaches that a CISO could have prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Target (2013)',
                impact: '$252M losses, 40M cards stolen',
                description:
                  "Attackers accessed Target's network via an HVAC vendor, installing malware on POS systems. A CISO would have implemented vendor access controls, network segmentation, and POS security monitoring to prevent this supply chain attack.",
              },
              {
                icon: AlertTriangle,
                title: 'Ticketmaster UK (2018)',
                impact: '£1.25M ICO fine, 9.4M customers affected',
                description:
                  "Payment data was stolen via a compromised third-party chatbot on Ticketmaster's website. A CISO would have conducted thorough supply chain security assessments and implemented monitoring to detect data exfiltration from third-party scripts.",
              },
              {
                icon: AlertTriangle,
                title: 'British Airways (2018)',
                impact: '£20M ICO fine (initially proposed at £183M), 400,000+ customers',
                description:
                  "A Magecart attack skimmed customer payment data over a 15-day period. The ICO initially proposed a £183M fine - the largest ever at the time - but this was reduced to £20M due to BA's representations and COVID-19 impact. A CISO would have implemented proper web application security and attack detection.",
              },
              {
                icon: AlertTriangle,
                title: 'Dixons Carphone (2017)',
                impact: '£500,000 ICO fine, 14 million records',
                description:
                  'A sustained cyber attack accessed 5.9 million payment cards and 10 million personal data records (later updated to 14 million). The retailer failed to secure their systems adequately. A CISO would have implemented proper network segmentation, intrusion detection, and timely security patching.',
              },
              {
                icon: AlertTriangle,
                title: "Sotheby's Home (2022)",
                impact: 'Credit card data skimmed for 9 months',
                description:
                  "The luxury retailer's website was infected with Magecart skimming malware. A CISO would have implemented web integrity monitoring, code review processes, and anomaly detection to identify unauthorised scripts quickly.",
              },
              {
                icon: AlertTriangle,
                title: 'Fat Face (2021)',
                impact: 'Customer data stolen, poor incident response',
                description:
                  'The UK clothing retailer suffered a breach and was criticised for taking weeks to notify customers. A CISO would have established incident response plans, detection capabilities, and communication protocols to respond more effectively.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks in Retail
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats that put customer trust and business viability at risk
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CreditCard,
                title: 'Magecart Attacks',
                description:
                  'Sophisticated skimming malware steals payment card data directly from checkout pages before it reaches secure servers.',
              },
              {
                icon: Truck,
                title: 'Supply Chain Compromise',
                description:
                  "Third-party scripts, payment processors, and logistics providers create pathways for attackers to reach your customers' data.",
              },
              {
                icon: ShoppingCart,
                title: 'Credential Stuffing',
                description:
                  'Attackers use stolen credentials from other breaches to access customer accounts and stored payment methods.',
              },
              {
                icon: Shield,
                title: 'DDoS & Seasonal Attacks',
                description:
                  'Peak shopping periods see increased attacks designed to disrupt sales when they matter most to your revenue.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Retail - FAQs"
        subtitle="Common questions from retailers and e-commerce businesses"
        items={[
          {
            id: 'retail-1',
            question: 'How does a fractional CISO work with retail businesses?',
            answer:
              'Our CISOs work with retailers and e-commerce businesses across the UK. They understand the seasonal pressures of retail, the complexity of omnichannel operations, and the critical importance of protecting customer payment and personal data.',
          },
          {
            id: 'retail-2',
            question: 'What regulations apply to retail cybersecurity?',
            answer:
              'Retailers must comply with PCI-DSS for payment card data, GDPR for customer personal data, and increasingly the security requirements of marketplace platforms and payment processors. E-commerce retailers face additional requirements around website security and third-party integrations.',
          },
          {
            id: 'retail-3',
            question: 'How does a fractional CISO help with PCI-DSS compliance?',
            answer:
              'Our CISOs guide retailers through PCI-DSS assessment, scope reduction, control implementation, and ongoing maintenance. We help you understand which Self-Assessment Questionnaire applies, reduce your cardholder data environment, and build the documentation that satisfies your acquiring bank.',
          },
          {
            id: 'retail-4',
            question: 'What is the biggest cyber threat to retailers?',
            answer:
              'Ransomware and Magecart-style attacks that steal payment card data from e-commerce websites. Retailers are attractive targets because they handle high volumes of payment data and cannot afford extended downtime during peak trading periods. A fractional CISO builds defences against both threats.',
          },
          {
            id: 'retail-5',
            question: 'Can a fractional CISO help during peak trading periods?',
            answer:
              'Yes. Our CISOs help retailers prepare for Black Friday, Christmas, and other peak periods by stress-testing payment systems, reviewing third-party integrations, and ensuring incident response plans are ready. We provide the security leadership that lets you focus on sales rather than breaches.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Customers and Your Brand
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            In retail, customer trust is everything. Our fractional CISOs help you protect payment
            data, maintain compliance, and defend against the threats targeting the sector.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
